// Health & Safety content for the worker signing page.
// Tool Box Talks must be acknowledged section-by-section. Site Rules follow.

export const TOOLBOX_TALKS = [
  {
    title: 'PNC Hand Arm Vibration Syndrome (HAVS) Prevention Awareness',
    body: [
      { h: 'What is HAVS and how to identify symptoms?' },
      { p: 'HAVS is a medical condition caused by prolonged exposure to vibration from hand-held power tools such as drills, sanders, grinders, and polishers. The continuous vibration can damage blood vessels, nerves, and muscles in the fingers, hands, and arms, leading to symptoms such as: Tingling or numbness in the fingers. Reduced sensitivity to cold or touch. Pain or aching in the fingers, hands, or arms. Loss of grip strength or difficulty in holding onto objects. Reduced dexterity and coordination in the hands or fingers. Swelling or whitening of the fingers, particularly in the cold. Raynaud\u2019s phenomenon, where fingers turn white, blue, or red in response to cold or stress. These symptoms can appear gradually over time and may worsen with continued exposure to hand-held vibrating tools.' },
      { h: 'How to prevent HAVS?' },
      { p: 'Prevention of HAVS involves taking measures to reduce exposure, some strategies are: Check tools before using them to make sure they have been properly maintained and repaired to avoid increased vibration caused by faults or general wear. Reduce the amount of time you use a tool in one go, by doing other jobs in between. Make sure any cutting tools are kept sharp so that they remain efficient. Always use the right tool for each job. Report any problems with your hands promptly to your employer or the person who does your health checks. Watch your grip! Hands stuck in a gripped position for a long-time can make it tougher for blood to reach them. Exercise your fingers and arms with simple moves every now and again, such as wiggling your fingers and making arm circles to improve circulation. Currently there is no known cure for HAVS, however early diagnosis can help manage symptoms and prevent further damage to the hands and arms.' },
    ],
  },
  {
    title: 'Consumption of Food in the Workplace',
    body: [
      { p: 'All PNC work areas are highly contaminated with dust particulates and other chemical agents due to the various processes carried out and therefore are NOT suitable for the consumption of food.' },
      { p: 'A rest area has been provided for this purpose and the company requests that all staff use this facility.' },
      { p: 'Welfare facilities are available, and we encourage that you clean your hands before eating.' },
    ],
  },
  {
    title: 'Manual Handling Awareness',
    body: [
      { p: 'Manual handling refers to the process of manually lifting, carrying, moving, pushing, and pulling objects or people. It is important for both employers and employees/contractors to have an awareness of manual handling to ensure that work can be carried out safely and effectively.' },
      { p: 'Manual handling is often considered a hazardous activity as it can lead to musculoskeletal disorders, if proper precautions aren\u2019t taken. Proper manual handling techniques can help prevent injuries and musculoskeletal disorders.' },
      { h: 'Key Techniques to Remember' },
      { list: [
        'Plan the lift by assessing the load and environment.',
        'Use correct posture.',
        'Get a good grip on the load.',
      ] },
      { p: 'Plan the lift: Assess the load and the environment before attempting the lift. Make sure the load is stable and balanced and that there is enough space to move around.' },
      { p: 'Use the correct posture: Keep your back straight, your shoulders relaxed, and your feet shoulder-width apart. Bend your knees and hips, not your back, to get into a squatting position.' },
      { p: 'Get a good grip: Use your hands to get a secure grip on the load. If possible, use gloves to improve your grip.' },
      { p: 'Lift smoothly and slowly: Keep the load close to your body as you lift and use the strength of your legs to push up. Avoid jerking or twisting movements. Move smoothly and slowly, taking small steps if necessary. Avoid sudden movements or changes in direction.' },
      { p: 'Lower with care: Lower the load slowly and carefully, bending your knees and hips, not your back. Put down then adjust.' },
      { p: 'Rest and recover: Take regular breaks and avoid overexertion. If you feel tired or in pain, stop and rest.' },
      { h: 'Additional considerations' },
      { list: [
        'Know your capabilities; only tackle jobs that you can handle.',
        'Ask for help if you need assistance.',
        'Make sure there is a clear walkway with good lighting to the work area.',
        'Wear gloves to protect against cuts and punctures.',
        'Wear safety footwear to protect from falling loads.',
      ] },
    ],
  },
  {
    title: 'Slips, Trips & Falls',
    body: [
      { p: 'Slips, trips, and falls are a common cause of injuries and accidents in the workplace. The main factors that contribute to slip, trip or fall accidents are: housekeeping, cleaning, flooring, lighting, environment, footwear, people.' },
      { h: 'Examples of slip, trip, and fall hazards' },
      { list: [
        'Wet or greasy floors, spills, and icy or snowy conditions can create slippery surfaces.',
        'Uneven or unstable surfaces can create tripping hazards.',
        'Cluttered walkways make it difficult to navigate and increase the risk of tripping.',
        'Inadequate lighting can make it difficult to see potential hazards.',
        'Inappropriate footwear can increase the risk of slipping and falling.',
        'Trailing cables and air lines, hand tools, engine components, etc., left on the ground may cause someone to trip.',
        'General rubbish such as damaged panels and masking paper can accumulate and form a tripping hazard.',
      ] },
      { h: 'How to prevent injuries from slips, trips and falls' },
      { list: [
        'Good housekeeping: tidy away tools, airlines, consumables and parts.',
        'Wear correct footwear with anti-slip soles.',
        'Report any defective or inadequate lighting.',
        'Watch out for uneven, holed, wet or newly cleaned floors. Report any defects.',
        'See it, sort it! Take responsibility for reporting, removing or avoiding hazards in your path.',
      ] },
    ],
  },
  {
    title: 'PPE Awareness',
    body: [
      { p: 'Examples of PPE include: respirators, hearing protection, safety goggles, foot protection, eye protection, protective gloves, high visibility clothing.' },
      { p: 'PPE will only protect the user of the PPE. It must be used in accordance with the manufacturer\u2019s instructions \u2014 NEVER MISUSE PPE. It must be in good condition to be fully effective \u2014 your health or life might depend on it.' },
      { p: 'PPE should be: appropriate for the task as identified in the risk assessment, properly stored when not being used, kept clean and in good repair.' },
      { p: 'Your responsibilities: never work without PPE when it is known to be necessary; return PPE to its safe storage after use; take reasonable care of your PPE and report loss or defects to your manager so it can be replaced.' },
    ],
  },
  {
    title: 'Skin & Respiratory Awareness',
    body: [
      { p: 'Work-related skin problems are caused or made worse by exposure to substances such as chemicals, and through having wet hands for long periods at work. Dermatitis (also known as eczema) is the most common, but urticaria and skin cancer are also problems.' },
      { h: 'Use the APC approach' },
      { p: 'Avoid \u2014 direct contact between unprotected hands and substances. Wear the correct PPE as stated in the COSHH risk assessment. Introduce controls (tools or equipment) to keep a safe working distance between skin and substances/products.' },
      { p: 'Protect \u2014 the skin. Use mild skin cleaning cream to wash off contamination promptly. Wash hands before eating, drinking and before wearing gloves. Dry thoroughly. Moisturise often \u2014 this replaces natural oils that keep skin\u2019s protective barrier working. Use suitable pre-work creams.' },
      { p: 'Check \u2014 hands regularly for the early signs of dermatitis or other skin problems. Symptoms: itchiness, dry skin, rash on swollen skin, blisters, flaking skin, redness. The earlier health effects are recognised and treated, the more likely a full recovery.' },
      { h: 'Respiratory awareness' },
      { p: 'Breathing in certain dusts, gases, fumes and vapours at work can cause serious, long-term lung damage \u2014 asbestosis, silicosis, COPD, asthma, emphysema, lung cancer.' },
      { list: [
        'Housekeeping \u2014 keep your area clean and tidy to stop build up of harmful dusts.',
        'PPE / RPE \u2014 wear the correct PPE/RPE as identified in the COSHH risk assessment; maintain it and report defects.',
        'Control \u2014 use aerosols in a spray booth or well-ventilated area. Do NOT blow off panels into the workshop. ALWAYS use the dust extraction system when sanding panels or filler.',
      ] },
    ],
  },
  {
    title: 'Working at Height',
    body: [
      { p: 'Falls from height are the most common cause of death and serious injury at work. They account for nearly 10% of injuries, mainly involving falls from ladders. "Work at height" means work in any place where, without precautions, a person could fall a distance liable to cause personal injury.' },
      { h: 'Assess the risks first' },
      { list: [
        'Height of the task',
        'Duration & frequency',
        'Condition of the surface being worked on',
      ] },
      { h: 'When working at height you should' },
      { list: [
        'Do as much as possible from the ground.',
        'Ensure you can safely get to and from where you are working.',
        'Ensure equipment is suitable, stable and strong enough for the job, maintained and checked regularly.',
        'Take precautions when working on or near fragile surfaces.',
        'Be competent in the job you are doing.',
      ] },
      { h: 'What not to do' },
      { list: [
        'Overload ladders \u2014 check the pictogram or label on the ladder.',
        'Overreach on ladders or stepladders.',
        'Rest a ladder against weak upper surfaces such as glazing or plastic gutters.',
        'Use ladders or stepladders for strenuous or heavy tasks \u2014 only use them for light work of short duration (max 30 minutes at a time).',
      ] },
    ],
  },
  {
    title: 'Fire Awareness',
    body: [
      { p: 'Fire awareness means understanding the potential causes of fires, and the practices and procedures to prevent them and respond to them.' },
      { p: 'Fire prevention: understand causes such as electrical faults, combustible materials or smoking. Practice good housekeeping, store flammable materials properly, ensure electrical systems and devices are installed and maintained correctly.' },
      { p: 'Fire detection: know how to detect fires early and how to respond to fire alarms.' },
      { p: 'Fire suppression: know how to use fire extinguishers and other suppression equipment, when to evacuate, and the different classes of fire with their correct extinguishing agents.' },
      { p: 'Emergency response: know how to respond, evacuate safely and where your nearest fire assembly point is. Practice fire drills.' },
      { h: 'What you can do' },
      { list: [
        'Keep your area clean and free from combustibles (dust, dry waste, impregnated solvent wipes).',
        'Store flammable materials in the correct COSHH cupboards when not in use and at the end of the day.',
        'Know your fire evacuation plan, fire exits, fire assembly point and fire marshals.',
        'Keep fire exits and equipment clear at all times.',
        'Check electrical equipment is in good working order before use. Report any defects.',
      ] },
      { p: 'Under The Fire Safety (Reform) Order 2005, PNC has a legal obligation to inform staff/contractors how fire extinguishers are labelled and what type of fire each is used for.' },
    ],
  },
  {
    title: 'Electrical Awareness',
    body: [
      { p: 'Electrical awareness means understanding electrical safety \u2014 how to use devices safely and how to identify potential hazards.' },
      { p: 'Electrical hazards can be life-threatening: electric shock and burns, explosion caused by unsuitable apparatus in explosive atmospheres, fire from faulty equipment, fatality \u2014 230 volts AC can kill.' },
      { p: 'Even non-fatal shocks can cause severe and permanent injury, for example shocks from faulty equipment may lead to falls from ladders, scaffolds or other work platforms.' },
      { h: 'Basic safety practices' },
      { list: [
        'Do not use electrical devices near water.',
        'Do not use damaged electrical cords.',
        'Pre-use visual check of equipment.',
        'Report defective equipment to your line manager.',
        'Never overload electrical outlets.',
        'Use electrical equipment only for its intended purpose.',
        'Switch off and unplug appliances before cleaning or adjusting them.',
      ] },
    ],
  },
  {
    title: 'COSHH Awareness',
    body: [
      { p: 'COSHH stands for Control of Substances Hazardous to Health. Hazardous substances (including chemicals) can harm if they enter the body. Examples:' },
      { list: [
        'Dusty or fume-laden air can cause lung diseases (e.g. welding, body repair).',
        'Metalworking fluids can grow bacteria and fungi which cause dermatitis and asthma.',
        'Wet working (catering, cleaning) can cause dermatitis.',
        'Exposure to certain sensitising agents can cause asthma.',
      ] },
      { h: 'Key principles' },
      { list: [
        'Risk assessment \u2014 identify substances, evaluate exposure and consider health effects.',
        'Control measures \u2014 engineering controls (ventilation), administrative controls (procedures, training) and PPE (air-fed masks, gloves, boots).',
        'Training and information \u2014 about risks, safe handling and storage, emergency response and proper PPE use.',
        'Health surveillance \u2014 monitor the workplace and worker health where required.',
      ] },
      { p: 'PNC\u2019s COSHH Risk Assessments are available in the Administration Office. Read the assessments for substances you use, follow the required controls, and report any adverse symptoms early.' },
    ],
  },
  {
    title: 'Risk Assessments',
    body: [
      { p: 'PNC has compiled the Company\u2019s General Risk Assessments for buildings. They can be accessed in the Administration Office or by scanning the QR code available on all Health & Safety notice boards within the workshop.' },
      { p: 'The purpose of the risk assessments is to ensure that staff are aware of specific hazards while working and to ensure those hazards are avoided. They provide guidance for your safety.' },
      { p: 'Risk assessments are organised by building. Obtain a copy of any risk assessment relevant to your building by contacting Tony Fleming (H&S Manager).' },
    ],
  },
  {
    title: 'Housekeeping & Waste Management',
    body: [
      { p: 'Maintaining good standards of housekeeping and waste management is essential. A clean and tidy working environment is halfway to a safe working environment. It is the responsibility of all employees at all levels \u2014 a continual, on-going process.' },
      { h: 'Why it matters' },
      { list: [
        'Reduce the number of slip, trip and fall accidents.',
        'Avoid delays caused by accidents and improve production (clean environment = clean paintwork).',
        'Give good first impressions to visitors (clients, HSE, other contractors).',
        'Reduce the risk of prosecution by complying with applicable laws.',
      ] },
      { h: 'What you can do' },
      { list: [
        'Remove waste from the working area and place in the correct bins (general, dry, contaminated).',
        'Place used aerosols in the correct bins.',
        'Return tools/equipment to designated storage once finished.',
        'Keep welfare facilities as you would expect to find them.',
        'Report hazards you cannot deal with to your supervisor/manager.',
        'DO NOT allow leads from tools to trail.',
        'DO NOT ignore spills of chemicals and oils \u2014 use the spill kits and report immediately.',
      ] },
    ],
  },
  {
    title: 'Noise Awareness',
    body: [
      { p: 'Loud noise can permanently damage hearing. Noise-induced hearing loss is the most common preventable occupational health hazard \u2014 there is no satisfactory treatment.' },
      { p: 'Within the workplace, excessive noise can come from loud music, plant equipment, machinery and production lines, electrical tools (drills), engines and even people. If you have to raise your voice to be heard by someone 2 m away, noise levels are too high.' },
      { p: 'Early signs of hearing loss: conversation becoming difficult or impossible, trouble using the telephone, difficulty in catching "t", "d" and "s" sounds, permanent tinnitus.' },
      { h: 'What you can do' },
      { list: [
        'Co-operate \u2014 help your employer protect your hearing. Use any noise control devices properly. Attend hearing checks.',
        'Look out for noise hazard signs on-site and obey them.',
        'Do not keep machinery running unnecessarily.',
        'Move the noise source away from the work area, or work away from the noise. Shield the noise process where possible.',
        'Wear hearing protection where given. Wear it properly and at all times in hearing protection areas. Removing it even briefly can damage your hearing.',
        'Report any problems with hearing protection or noise control devices straight away.',
      ] },
    ],
  },
];

export const SITE_RULES = [
  'High Vis vests MUST be worn at all times, other than in exceptional circumstances, in all areas due to vehicle movements on site.',
  'Safety footwear MUST be worn when working on site.',
  'All job specific PPE MUST be worn when on site.',
  'Dust extraction MUST be used when doing any sanding processes.',
  'Areas MUST be kept clean and tidy.',
  'The possession/consumption of illegal substances or alcohol is strictly prohibited.',
  'The site speed limits MUST be adhered to at all times. (20MPH outside / 5MPH inside)',
  'Theft or misuse of company property will result in disciplinary action.',
  'Smoking must ONLY take place in designated areas.',
  'Eating must ONLY take place in designated areas and never inside the workshop.',
];
